import React, { useEffect, useRef, useState } from "react";
import styled, { useTheme } from "styled-components";
import FoundContactElement from "../Chat/FoundContactElement";
import { useTranslate } from '@tolgee/react';
import SearchMenu from "../GenericComponents/SearchMenu";
import { VariableSizeList as List } from "react-window"; // Import react-window
import { isArray } from "lodash";
import { ReactComponent as CloseBtn } from "../../Assets/iconsv2/generic/closeBtn.svg";
import { useGenericComponentsStateService } from "../../Context/GenericComponentsStateContext";
import { RotatingLines } from "react-loader-spinner";

const AddNewCallModal = ({
  contacts,
  handleCreateCallOnContactClick,
  handleInviteContact,
}) => {
  const [searchValue, setSearchValue] = useState("");
  const [listHeight, setListHeight] = useState(0);
  const [allContacts, setAllContacts] = useState([]);
  const [isLoading, setIsLoading] = useState(true)

  const { setIsAddNewCallOpen, isAddNewCallOpen } =
    useGenericComponentsStateService();
  const listRef = useRef();
  const listContainerRef = useRef();
  const themeColors = useTheme()

  const handleCloseModla = (e) => {
    setIsAddNewCallOpen(false);
  };

  const searchContact = (list) => {
    const searchTerm = searchValue?.toLowerCase() || "";

    if (searchTerm === "") {
      return list;
    }

    return list.filter((item) => {
      const name = item?.name?.toLowerCase();
      return name?.includes(searchTerm); // Match anywhere in the string
    });
  };

  const getItemSize = (index) => {
    const contact = allContacts[index];
    if (!contact) return 56; // Default size to prevent errors
    return contact.type === "header" ? 46 : 56;
  };

  const insertContactTypeHeader = (arr) => {
    let newArr = [...arr];
    let hasContacts = newArr.some((item) => item.type === "contact");
    let hasOutsideContacts = newArr.some((item) => item.type === "outside");

    if (hasContacts) {
      newArr.unshift({ type: "header", value: t('new_chat.contacts_on_emotii') }); // Always at the start
    }

    if (hasOutsideContacts) {
      let indexB = newArr.findIndex((item) => item.type === "outside");
      newArr.splice(indexB, 0, { type: "header", value: t('new_chat.invite_contacts') });
    }

    return newArr;
  };

  useEffect(() => {
    if (!contacts) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    let mappedContacts = [];
    let mappedOutsideContacts = [];
    let allContacts = [];

    mappedContacts = contacts?.on_app_contacts?.map((item) => {
      return {
        ...item,
        type: "contact",
      };
    });
    mappedOutsideContacts = contacts?.outside_contacts?.map((item) => {
      return {
        ...item,
        type: "outside",
      };
    });

    if (isArray(mappedContacts) && isArray(mappedOutsideContacts)) {
      allContacts = [...mappedContacts, ...mappedOutsideContacts];
    } else {
      allContacts = [];
    }

    let availableContacs = searchContact(allContacts);
    let availableContacsWithHeaders = insertContactTypeHeader(availableContacs);

    setAllContacts(availableContacsWithHeaders);
    setIsLoading(false);
  }, [contacts, searchValue]);

  useEffect(() => {
    const updateHeight = () => {
      if (listContainerRef.current) {
        if (allContacts.length < 30) {
          setListHeight(allContacts.length * 56);
        } else {
          setListHeight(30 * 56)
        }
      }
    };

    updateHeight(); // Set height on mount
    window.addEventListener("resize", updateHeight); // Update on resize
    return () => window.removeEventListener("resize", updateHeight);
  }, [listContainerRef.current, allContacts]);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.resetAfterIndex(0, true); // Force remeasure
    }
  }, [allContacts]);

  const { t } = useTranslate();
  return (
    <Container>
      <MenuContainer>
        <ContentWrapper>
          <ContentHeader>
            <Title>{t('new_chat.new_call')}</Title>
            <CloseButton onClick={() => handleCloseModla()}>
              <CloseBtn />
            </CloseButton>
          </ContentHeader>

          <CardInputsWrapper>
            <OptionsWrapper>
           <SearchMenu value={searchValue} setvalue={setSearchValue} />
           </OptionsWrapper>
            {isLoading ?
              <SpinnerWrapper>
                <RotatingLines
                  strokeColor={themeColors.menu.selectedTextColor}
                  strokeWidth={3}
                  animationDuration="0.75"
                  width={50}
                  visible={true}
                />
              </SpinnerWrapper> :
              <ContactListWrapper ref={listContainerRef}>
                <List
                  ref={listRef}
                  height={listHeight} // Adjust this based on the height of the container
                  itemCount={allContacts.length}
                  itemSize={getItemSize} // Adjust based on the size of each contact element
                  width="100%" // Full width of the container
                >
                  {({ index, style }) => {
                    const contact = allContacts[index];

                    if (contact.type === "header") {
                      return (
                        <SubtitleWrapper
                          key={index}
                          style={{ ...style, padding: "0 16px" }}
                        >
                          <Subtitle>{contact.value}</Subtitle>
                        </SubtitleWrapper>
                      );
                    }
                    if (contact.type === "contact") {
                      return (
                        <FoundContactElement
                          style={style}
                          key={contact.id}
                          item={contact}
                          callAction={handleCreateCallOnContactClick}
                          handleSelectContact={handleCreateCallOnContactClick}
                          noCheck
                        />
                      );
                    }
                    if (contact.type === "outside") {
                      return (
                        <FoundContactElement
                          style={style}
                          key={index}
                          item={contact}
                          invite
                          handleInvite={handleInviteContact}
                          noCheck
                        />
                      );
                    }
                  }}
                </List>
              </ContactListWrapper>
            }
          </CardInputsWrapper>
        </ContentWrapper>
      </MenuContainer>
    </Container>
  );
};

export default AddNewCallModal;

const Container = styled.div`
  background: ${({ theme }) => theme.chat.conversationBackground};
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  height: 100%;
  margin: auto;
  overflow: auto;
  border: 1px solid ${({ theme }) => theme.sidebar.borderColor};

`;

const MenuContainer = styled.div`
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.menu.background};
  height: 100%;
  overflow: hidden;
  width: 100%;
  gap: 16px;

`;

const Title = styled.p`
  color: ${({ theme }) => theme.menu.primaryTextColor};
  font-weight: 700;
  font-size: 16px;
  line-height: 32px;
  letter-spacing: 0.04px;
  vertical-align: middle;
`;

const OptionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 0 16px; 
  padding-bottom: 16px; /* Added bottom padding to create distance below Search */
`;

const ContentWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  overflow: hidden;
  gap: 16px;
  background: ${({ theme }) => theme.chat.conversationBackground};
`;

const ContentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 19px 16px;
  align-items: center;
  background: ${({ theme }) => theme.chat.conversationBackground};
  border-bottom: 1px solid ${({ theme }) => theme.sidebar.borderColor};
  gap: 20px;
`;
const ContactListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: auto;
`;

const SubtitleWrapper = styled.div`
  display: flex;
  align-items: center;
`;
const Subtitle = styled.p`
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  font-size: 14px;
  font-weight: 500;
  line-height: 16px;
  letter-spacing: 0.04px;
  vertical-align: middle;
  text-transform: uppercase;
`;

const CloseButton = styled.button`
border:none;
background:none;
cursor:pointer;
display:flex;
align-items:center;
justify-content:center;

svg{
height:24px;
width24px;
color:${({ theme }) => theme.menu.selectedOpionBackground};
stroke:${({ theme }) => theme.sidebar.borderColor};
}
`;
const CardInputsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0 auto;
  width: 100%;
  max-width: 600px;
  overflow: hidden;
  height: 100%;
`;

const SpinnerWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  padding:20px;
`;
